import { useState } from "react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";

export default function ContactPage() {
  const [status, setStatus] = useState<"idle" | "sending" | "sent" | "error">("idle");

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new URLSearchParams();
    const fields = ["name", "email", "phone", "subject", "message"];
    fields.forEach((f) => {
      const el = form.elements.namedItem(f) as HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement | null;
      if (el) data.append(f, el.value);
    });
    setStatus("sending");
    try {
      await fetch("https://readdy.ai/api/form/d7lhh1ate7rh0002nd8g", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: data.toString(),
      });
      setStatus("sent");
      form.reset();
    } catch {
      setStatus("error");
    }
  };

  return (
    <main className="min-h-screen bg-[#0d0d0d]">
      <Navbar />

      {/* Page header */}
      <section className="w-full pt-36 pb-16 md:pt-44 md:pb-20 px-6 md:px-10 lg:px-16">
        <p
          className="text-[11px] uppercase tracking-[0.28em] text-[#B8422E] mb-4"
          style={{ fontFamily: "'Space Grotesk', sans-serif" }}
        >
          / Contact Us
        </p>
        <h1
          className="text-4xl md:text-5xl lg:text-[60px] font-extrabold text-white leading-[1.04] tracking-[-0.025em]"
          style={{ fontFamily: "'Public Sans', sans-serif" }}
        >
          Let&apos;s Talk
        </h1>
      </section>

      {/* Main content */}
      <section className="w-full px-6 md:px-10 lg:px-16 pb-24 md:pb-32">
        <div className="bg-[#111213] p-8 md:p-10">
            {status === "sent" ? (
              <div className="h-full flex flex-col items-center justify-center py-20 text-center">
                <div className="w-12 h-12 flex items-center justify-center border border-[#B8422E]/40 mb-5" style={{ borderRadius: "4px" }}>
                  <i className="ri-check-line text-xl text-[#B8422E]" />
                </div>
                <p
                  className="text-lg font-bold text-white mb-2"
                  style={{ fontFamily: "'Public Sans', sans-serif" }}
                >
                  Message Sent
                </p>
                <p
                  className="text-sm text-[#6C7278]"
                  style={{ fontFamily: "'Public Sans', sans-serif" }}
                >
                  We&apos;ll get back to you within one business day.
                </p>
                <button
                  onClick={() => setStatus("idle")}
                  className="mt-8 text-[11px] uppercase tracking-[0.16em] text-[#6C7278] hover:text-white transition-colors cursor-pointer"
                  style={{ fontFamily: "'Space Grotesk', sans-serif" }}
                >
                  Send another message
                </button>
              </div>
            ) : (
              <form
                id="contact-form"
                data-readdy-form
                onSubmit={handleSubmit}
                className="flex flex-col gap-5"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div className="flex flex-col gap-1.5">
                    <label
                      className="text-[10px] uppercase tracking-[0.18em] text-[#6C7278]"
                      style={{ fontFamily: "'Space Grotesk', sans-serif" }}
                    >
                      Name
                    </label>
                    <input
                      type="text"
                      name="name"
                      required
                      placeholder="Your full name"
                      className="h-11 px-4 bg-white/[0.04] border border-white/[0.08] text-white text-sm placeholder:text-white/20 focus:outline-none focus:border-[#B8422E] transition-colors"
                      style={{ fontFamily: "'Public Sans', sans-serif", borderRadius: "4px" }}
                    />
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <label
                      className="text-[10px] uppercase tracking-[0.18em] text-[#6C7278]"
                      style={{ fontFamily: "'Space Grotesk', sans-serif" }}
                    >
                      Email
                    </label>
                    <input
                      type="email"
                      name="email"
                      required
                      placeholder="your@email.com"
                      className="h-11 px-4 bg-white/[0.04] border border-white/[0.08] text-white text-sm placeholder:text-white/20 focus:outline-none focus:border-[#B8422E] transition-colors"
                      style={{ fontFamily: "'Public Sans', sans-serif", borderRadius: "4px" }}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div className="flex flex-col gap-1.5">
                    <label
                      className="text-[10px] uppercase tracking-[0.18em] text-[#6C7278]"
                      style={{ fontFamily: "'Space Grotesk', sans-serif" }}
                    >
                      Phone <span className="normal-case tracking-normal opacity-50">(optional)</span>
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      placeholder="+44 000 000 0000"
                      className="h-11 px-4 bg-white/[0.04] border border-white/[0.08] text-white text-sm placeholder:text-white/20 focus:outline-none focus:border-[#B8422E] transition-colors"
                      style={{ fontFamily: "'Public Sans', sans-serif", borderRadius: "4px" }}
                    />
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <label
                      className="text-[10px] uppercase tracking-[0.18em] text-[#6C7278]"
                      style={{ fontFamily: "'Space Grotesk', sans-serif" }}
                    >
                      Subject
                    </label>
                    <select
                      name="subject"
                      required
                      className="h-11 px-4 bg-[#1A1C1E] border border-white/[0.08] text-white text-sm focus:outline-none focus:border-[#B8422E] transition-colors cursor-pointer"
                      style={{ fontFamily: "'Public Sans', sans-serif", borderRadius: "4px" }}
                      defaultValue=""
                    >
                      <option value="" disabled>Select a topic</option>
                      <option value="Software Development">Software Development</option>
                      <option value="IT Consulting">IT Consulting</option>
                      <option value="Digital Marketing">Digital Marketing</option>
                      <option value="Product Demo">Product Demo</option>
                      <option value="Partnership">Partnership</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                </div>

                <div className="flex flex-col gap-1.5">
                  <label
                    className="text-[10px] uppercase tracking-[0.18em] text-[#6C7278]"
                    style={{ fontFamily: "'Space Grotesk', sans-serif" }}
                  >
                    Message
                  </label>
                  <textarea
                    name="message"
                    required
                    rows={5}
                    maxLength={500}
                    placeholder="Tell us about your project or inquiry..."
                    className="px-4 py-3 bg-white/[0.04] border border-white/[0.08] text-white text-sm placeholder:text-white/20 focus:outline-none focus:border-[#B8422E] transition-colors resize-none"
                    style={{ fontFamily: "'Public Sans', sans-serif", borderRadius: "4px" }}
                  />
                </div>

                {status === "error" && (
                  <p className="text-sm text-[#B8422E]" style={{ fontFamily: "'Public Sans', sans-serif" }}>
                    Something went wrong. Please try again.
                  </p>
                )}

                <button
                  type="submit"
                  disabled={status === "sending"}
                  className="self-start inline-flex items-center gap-2 bg-[#B8422E] text-white px-8 py-3.5 text-[12px] font-semibold hover:bg-[#9E3826] disabled:opacity-60 transition-colors duration-300 whitespace-nowrap cursor-pointer"
                  style={{ fontFamily: "'Space Grotesk', sans-serif", borderRadius: "4px" }}
                >
                  {status === "sending" ? (
                    <><i className="ri-loader-4-line animate-spin" /> Sending...</>
                  ) : (
                    <>Send Message <i className="ri-arrow-right-line" /></>
                  )}
                </button>
              </form>
            )}
          </div>
      </section>

      <Footer />
    </main>
  );
}
